import { Box } from '@atgfr8/atg-ui-tailwind';
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  CloseIcon,
  FullscreenExitIcon,
  FullscreenIcon,
  MinusCircleIcon,
  PlusIcon,
} from '@atgfr8/icons';
import { BasicModal } from '@components/Modal/BasicModal';
import { ToastType } from '@components/Toast/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { useNavigate } from '@solidjs/router';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { sendDirectMessage } from '@store/Global/service';
import { closeModal, openModal } from '@store/modals';
import type {
  ActionElement,
  MessageAttachment,
  MessageBlock,
  MessageField,
  MessagePayload,
  MessageText,
} from '@typeDefinitions/messagePayload';
import {
  buildAnnouncementPayload,
  resolveActionElement,
  resolveServeDocumentSrc,
} from '@utils/appMessage';
import { markdownToHtml } from '@utils/markdownToHtml';
import { handleToast } from '@utils/utils';
import {
  For,
  Show,
  createEffect,
  createSignal,
  createUniqueId,
  lazy,
  onCleanup,
} from 'solid-js';

const RichTextEditor = lazy(
  () => import('@components/RichTextEditor/RichTextEditor'),
);
const isCapacityRichBidAlertEnabled = isFeatureFlagEnabled(
  FeatureFlags.FeatureCapacityRichBidAlert,
);

const formatFileSize = (bytes?: number) => {
  if (bytes == null) {
    return '';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const asOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const resolveViewerSrc = (value: unknown) =>
  resolveServeDocumentSrc(asOptionalString(value) ?? '');

const MarkdownText = (props: {
  text: string;
  class?: string;
  onOpenImage?: (
    url: string,
    altText?: string,
    meta?: { width?: number; height?: number },
  ) => void;
}) => {
  const openFromEvent = (event: MouseEvent | KeyboardEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !props.onOpenImage) {
      return;
    }
    if (target.closest('a')) {
      return;
    }
    const image = target.closest('img[data-inline-image-src]');
    if (!(image instanceof HTMLImageElement)) {
      return;
    }
    event.preventDefault();
    props.onOpenImage(
      image.dataset.inlineImageSrc ?? image.src,
      image.dataset.inlineImageAlt ?? image.alt,
    );
  };

  return (
    <span
      class={`[&_strong]:font-bold [&_em]:italic [&_a]:text-[#026EA1] [&_a]:underline [&_code]:rounded [&_code]:bg-[#e2e8f0] [&_code]:px-1 ${props.class ?? ''}`}
      innerHTML={markdownToHtml(props.text)}
      onClick={openFromEvent}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') {
          return;
        }
        openFromEvent(event);
      }}
    />
  );
};

const PlainText = (props: {
  text: MessageText;
  class?: string;
  onOpenImage?: (url: string, altText?: string) => void;
}) => (
  <Show
    when={props.text.type === 'mrkdwn'}
    fallback={<span class={props.class}>{props.text.text}</span>}
  >
    <MarkdownText
      text={props.text.text}
      class={props.class}
      onOpenImage={props.onOpenImage}
    />
  </Show>
);

const ActionButtons = (props: {
  elements: ActionElement[];
  onNavigate: (url: string) => void;
  payload: MessagePayload;
}) => (
  <div class="flex flex-wrap gap-2 mt-2">
    <For each={props.elements}>
      {(el) => (
        <button
          type="button"
          class="rounded border border-[#80b6cf] bg-white px-2 py-1 text-xs font-semibold text-[#026EA1] hover:bg-[#E8F0F6]"
          data-testid={`message-action-${el.actionId}`}
          onClick={() => {
            const link = resolveActionElement(el, props.payload);
            if (link?.actionUrl) {
              props.onNavigate(link.actionUrl);
            }
          }}
        >
          {el.text.text}
        </button>
      )}
    </For>
  </div>
);

const BlockView = (props: {
  block: MessageBlock;
  payload: MessagePayload;
  onNavigate: (url: string) => void;
  onOpenImage: (
    url: string,
    altText?: string,
    meta?: { width?: number; height?: number },
  ) => void;
}) => {
  const block = props.block;
  if (block.type === 'divider') {
    return <hr class="my-2 border-[#d5deea]" />;
  }
  if (block.type === 'image' && block.url) {
    const inlineUrl = resolveViewerSrc(block.url);
    const rawUrl = resolveViewerSrc(block.rawUrl ?? block.url);
    return (
      <button
        type="button"
        class="mt-2 block max-w-full cursor-zoom-in"
        data-testid="message-inline-image"
        onClick={() =>
          props.onOpenImage(rawUrl, block.altText, {
            width: block.width,
            height: block.height,
          })
        }
      >
        <img
          src={inlineUrl}
          alt={block.altText ?? ''}
          class="max-h-40 max-w-full rounded"
        />
      </button>
    );
  }
  if (block.type === 'header' && block.text) {
    return (
      <div class="text-sm font-semibold text-[#123B50]">
        <PlainText text={block.text} onOpenImage={props.onOpenImage} />
      </div>
    );
  }
  if (block.type === 'section') {
    return (
      <div class="text-[13px] text-[#808080] leading-snug">
        <Show when={block.text}>
          {(text) => (
            <PlainText text={text()} onOpenImage={props.onOpenImage} />
          )}
        </Show>
        <Show when={block.fields && block.fields.length > 0}>
          <div class="mt-1 grid grid-cols-2 gap-1">
            <For each={block.fields}>
              {(field: MessageField) => (
                <MarkdownText
                  text={field.text}
                  class="text-[12px] text-[#808080]"
                  onOpenImage={props.onOpenImage}
                />
              )}
            </For>
          </div>
        </Show>
      </div>
    );
  }
  if (block.type === 'context' && block.elements) {
    return (
      <div class="mt-1 flex flex-wrap gap-x-2 text-[11px] text-[#9ca3af]">
        <For each={block.elements}>
          {(el) => <PlainText text={el} onOpenImage={props.onOpenImage} />}
        </For>
      </div>
    );
  }
  if (block.type === 'actions' && block.actionElements) {
    return (
      <ActionButtons
        elements={block.actionElements}
        payload={props.payload}
        onNavigate={props.onNavigate}
      />
    );
  }
  return null;
};

const AttachmentRow = (props: { attachment: MessageAttachment }) => {
  const href = () =>
    resolveViewerSrc(props.attachment.rawUrl ?? props.attachment.url);
  const label = () =>
    props.attachment.title || props.attachment.fileName || 'Attachment';
  const sizeLabel = () => formatFileSize(props.attachment.fileSize);

  return (
    <a
      href={href()}
      target="_blank"
      rel="noopener noreferrer"
      class="mt-2 flex items-center justify-between gap-2 rounded border border-[#d5deea] bg-white px-2 py-1.5 text-[12px] text-[#026EA1] hover:underline"
      style={
        props.attachment.color
          ? { 'border-left': `3px solid ${props.attachment.color}` }
          : undefined
      }
      data-testid="message-attachment"
    >
      <span class="truncate">{label()}</span>
      <Show when={sizeLabel()}>
        <span class="shrink-0 text-[11px] text-[#808080] no-underline">
          {sizeLabel()}
        </span>
      </Show>
    </a>
  );
};

const sameResolvedUrl = (left?: string, right?: string) => {
  if (!left || !right) {
    return false;
  }
  return resolveViewerSrc(left) === resolveViewerSrc(right);
};

type ImagePreview = {
  url: string;
  altText?: string;
  width?: number;
  height?: number;
};

type DirectMessageRecipient = {
  id: number;
  label: string;
};

const shouldOpenFullscreen = (width?: number, height?: number) => {
  if (typeof window === 'undefined' || width == null || height == null) {
    return false;
  }

  return width > window.innerWidth * 0.85 || height > window.innerHeight * 0.8;
};

export const MessageCard = (props: { payload: MessagePayload }) => {
  const navigate = useNavigate();
  let imageViewportRef: HTMLDivElement | undefined;
  let fullscreenRootRef: HTMLDivElement | undefined;
  const [selectedImage, setSelectedImage] = createSignal<ImagePreview | null>(
    null,
  );
  const [zoom, setZoom] = createSignal(1);
  const [isFullscreen, setIsFullscreen] = createSignal(false);
  const [directMessageRecipient, setDirectMessageRecipient] =
    createSignal<DirectMessageRecipient | null>(null);
  const [directMessage, setDirectMessage] = createSignal('');
  const [isSendingDirectMessage, setIsSendingDirectMessage] =
    createSignal(false);
  const [isDirectMessageModalOpen, setIsDirectMessageModalOpen] =
    createSignal(false);
  const imageModalId = `message-inline-image-preview-modal-${createUniqueId()}`;
  const directMessageModalId = `message-direct-compose-modal-${createUniqueId()}`;
  const panStep = 120;
  const minZoom = 0.05;
  const maxZoom = 4;

  const clampZoom = (value: number) =>
    Math.min(maxZoom, Math.max(minZoom, value));
  const imageViewportHeight = () =>
    isFullscreen() ? 'calc(100vh - 72px)' : '85vh';

  const panImage = (left: number, top: number) => {
    imageViewportRef?.scrollBy({ left, top, behavior: 'smooth' });
  };

  const readViewportSize = () => {
    if (!imageViewportRef) {
      return { width: 0, height: 0 };
    }
    const styles = window.getComputedStyle(imageViewportRef);
    const padX =
      Number.parseFloat(styles.paddingLeft) +
      Number.parseFloat(styles.paddingRight);
    const padY =
      Number.parseFloat(styles.paddingTop) +
      Number.parseFloat(styles.paddingBottom);
    return {
      width: Math.max(imageViewportRef.clientWidth - padX, 0),
      height: Math.max(imageViewportRef.clientHeight - padY, 0),
    };
  };

  const getRenderedImageSize = () => {
    const rendered = imageViewportRef?.querySelector('img');
    const selected = selectedImage();
    return {
      width: selected?.width || rendered?.naturalWidth || undefined,
      height: selected?.height || rendered?.naturalHeight || undefined,
    };
  };

  const getFitZoom = (width?: number, height?: number) => {
    const viewport = readViewportSize();
    if (!width || !height || viewport.width <= 0 || viewport.height <= 0) {
      return 1;
    }

    return clampZoom(
      Math.min(viewport.width / width, viewport.height / height),
    );
  };

  const fitViewport = (width?: number, height?: number) => {
    const size = width && height ? { width, height } : getRenderedImageSize();
    setZoom(getFitZoom(size.width, size.height));
    imageViewportRef?.scrollTo({
      left: 0,
      top: 0,
      behavior: 'instant' as ScrollBehavior,
    });
  };

  const scheduleFit = (width?: number, height?: number, attempt = 0) => {
    requestAnimationFrame(() => {
      const viewport = readViewportSize();
      const size = width && height ? { width, height } : getRenderedImageSize();
      if (
        attempt < 8 &&
        (viewport.width <= 0 ||
          viewport.height <= 0 ||
          !size.width ||
          !size.height)
      ) {
        scheduleFit(size.width, size.height, attempt + 1);
        return;
      }
      fitViewport(size.width, size.height);
    });
  };

  const applyNaturalImageSize = (loadedImage: HTMLImageElement) => {
    const nextWidth = loadedImage.naturalWidth;
    const nextHeight = loadedImage.naturalHeight;
    if (!nextWidth || !nextHeight) {
      return;
    }
    setSelectedImage((current) => {
      if (!current) {
        return current;
      }
      if (current.width === nextWidth && current.height === nextHeight) {
        return current;
      }
      return {
        ...current,
        width: nextWidth,
        height: nextHeight,
      };
    });
  };

  const resetViewport = () => {
    const size = getRenderedImageSize();
    fitViewport(size.width, size.height);
  };

  const toggleFullscreen = async () => {
    if (!fullscreenRootRef) {
      return;
    }
    if (document.fullscreenElement === fullscreenRootRef) {
      await document.exitFullscreen();
      return;
    }
    await fullscreenRootRef.requestFullscreen();
  };

  createEffect(() => {
    const image = selectedImage();
    if (image) {
      scheduleFit(image.width, image.height);
    }
  });

  const syncFullscreen = () => {
    setIsFullscreen(document.fullscreenElement === fullscreenRootRef);
  };

  document.addEventListener('fullscreenchange', syncFullscreen);
  onCleanup(() => {
    document.removeEventListener('fullscreenchange', syncFullscreen);
  });

  const blocks = () => {
    if (props.payload.blocks && props.payload.blocks.length > 0) {
      return props.payload.blocks;
    }
    if (props.payload.markdown) {
      return [
        {
          type: 'section' as const,
          text: { type: 'mrkdwn' as const, text: props.payload.markdown },
        },
      ];
    }
    if (props.payload.text) {
      return [
        {
          type: 'section' as const,
          text: { type: 'plain_text' as const, text: props.payload.text },
        },
      ];
    }
    return [];
  };

  const onNavigate = (url: string) => {
    navigate(url);
  };

  const openDirectMessage = (event: MouseEvent) => {
    if (!isCapacityRichBidAlertEnabled()) {
      return;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return;
    }
    const link = target.closest('a');
    const href = link?.getAttribute('href');
    if (!href?.startsWith('/notification/direct-message/compose')) {
      return;
    }

    const url = new URL(href, window.location.origin);
    const recipientId = Number(url.searchParams.get('targetUserId'));
    const label = url.searchParams.get('targetLabel') ?? 'recipient';
    if (!Number.isInteger(recipientId) || recipientId <= 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setDirectMessageRecipient({ id: recipientId, label });
    setDirectMessage('');
    setIsDirectMessageModalOpen(true);
    openModal(directMessageModalId);
  };

  const submitDirectMessage = async () => {
    const recipient = directMessageRecipient();
    const content = directMessage().trim();
    if (!recipient || !content || isSendingDirectMessage()) {
      return;
    }

    setIsSendingDirectMessage(true);
    try {
      const sent = await sendDirectMessage(
        recipient.id,
        buildAnnouncementPayload(content),
      );
      if (sent) {
        handleToast(ToastType.Success, 'Message sent');
        closeModal(directMessageModalId);
        setIsDirectMessageModalOpen(false);
        setDirectMessageRecipient(null);
        setDirectMessage('');
      }
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    } finally {
      setIsSendingDirectMessage(false);
    }
  };

  const resolveImagePreview = (url: string, altText?: string): ImagePreview => {
    const attachment = (props.payload.attachments ?? []).find(
      (item) =>
        sameResolvedUrl(asOptionalString(item.thumbnailUrl), url) ||
        sameResolvedUrl(asOptionalString(item.url), url) ||
        sameResolvedUrl(asOptionalString(item.rawUrl), url),
    );

    return {
      url: resolveViewerSrc(attachment?.rawUrl ?? url),
      altText: altText ?? attachment?.altText,
      width: attachment?.width,
      height: attachment?.height,
    };
  };

  const onOpenImage = (
    url: string,
    altText?: string,
    meta?: { width?: number; height?: number },
  ) => {
    const preview = resolveImagePreview(url, altText);
    setSelectedImage({
      ...preview,
      width: preview.width ?? meta?.width,
      height: preview.height ?? meta?.height,
    });
    openModal(imageModalId);

    if (
      shouldOpenFullscreen(
        preview.width ?? meta?.width,
        preview.height ?? meta?.height,
      )
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (
            fullscreenRootRef &&
            document.fullscreenElement !== fullscreenRootRef
          ) {
            void fullscreenRootRef.requestFullscreen();
          }
        });
      });
    }
  };

  const onCloseImage = () => {
    if (document.fullscreenElement === fullscreenRootRef) {
      void document.exitFullscreen();
    }
    setIsFullscreen(false);
    setZoom(1);
    setSelectedImage(null);
  };

  const closeImageModal = () => {
    onCloseImage();
    closeModal(imageModalId);
  };

  const attachments = () => props.payload.attachments ?? [];

  return (
    <Box
      class="w-full min-w-0"
      style={{ 'word-break': 'break-word' }}
      onClick={openDirectMessage}
    >
      <BasicModal
        id={directMessageModalId}
        title={`Message ${directMessageRecipient()?.label ?? ''}`}
        width="640px"
        onSubmitText="Send"
        onSubmit={submitDirectMessage}
        onSubmitLoading={isSendingDirectMessage()}
        preventCloseOnSubmit
        onClose={() => {
          setIsDirectMessageModalOpen(false);
          setDirectMessageRecipient(null);
          setDirectMessage('');
        }}
      >
        <Show when={isDirectMessageModalOpen()}>
          <RichTextEditor
            value={directMessage()}
            onChange={setDirectMessage}
            maxLength={10_000}
            ariaLabel="Direct message"
            placeholder="Write a message"
          />
        </Show>
      </BasicModal>
      <BasicModal
        id={imageModalId}
        header={false}
        width="min(96vw, 1600px)"
        maxWidth="min(96vw, 1600px)"
        onClose={onCloseImage}
        showClose={false}
        noCloseOnClickOutside={false}
        backdropClass="bg-black/75"
        mainContainerStyles={{ padding: '0' }}
      >
        <Show when={selectedImage()}>
          {(image) => (
            <div ref={fullscreenRootRef} class="bg-black text-white">
              <div class="flex flex-wrap items-center justify-end gap-2 border-b border-white/15 px-3 py-2">
                <a
                  href={image().url}
                  download={image().altText ?? 'announcement-image'}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="flex h-11 min-w-11 items-center justify-center rounded border border-white/20 bg-white/10 px-3 text-xs font-semibold hover:bg-white/20"
                  data-testid="message-image-download"
                >
                  Download raw
                </a>
                <button
                  type="button"
                  aria-label="Zoom out"
                  class="flex h-11 min-w-11 items-center justify-center rounded border border-white/20 bg-white/10 px-3 hover:bg-white/20"
                  onClick={() => setZoom((value) => clampZoom(value - 0.1))}
                >
                  <MinusCircleIcon size={18} />
                </button>
                <button
                  type="button"
                  aria-label="Zoom in"
                  class="flex h-11 min-w-11 items-center justify-center rounded border border-white/20 bg-white/10 px-3 hover:bg-white/20"
                  onClick={() => setZoom((value) => clampZoom(value + 0.1))}
                >
                  <PlusIcon size={18} />
                </button>
                <button
                  type="button"
                  aria-label="Fit image and reset pan"
                  class="h-11 min-w-11 rounded border border-white/20 bg-white/10 px-3 text-xs font-semibold hover:bg-white/20"
                  onClick={resetViewport}
                >
                  Fit
                </button>
                <button
                  type="button"
                  aria-label={
                    isFullscreen() ? 'Exit fullscreen' : 'Enter fullscreen'
                  }
                  class="flex h-11 min-w-11 items-center justify-center rounded border border-white/20 bg-white/10 px-3 hover:bg-white/20"
                  onClick={() => {
                    void toggleFullscreen();
                  }}
                >
                  <Show
                    when={isFullscreen()}
                    fallback={<FullscreenIcon size={18} />}
                  >
                    <FullscreenExitIcon size={18} />
                  </Show>
                </button>
                <div class="flex items-center gap-1 rounded border border-white/20 bg-white/10 p-1">
                  <button
                    type="button"
                    aria-label="Pan left"
                    class="flex h-9 w-9 items-center justify-center rounded hover:bg-white/20"
                    onClick={() => panImage(-panStep, 0)}
                  >
                    <ArrowLeftIcon size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Pan up"
                    class="flex h-9 w-9 items-center justify-center rounded hover:bg-white/20"
                    onClick={() => panImage(0, -panStep)}
                  >
                    <ArrowUpIcon size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Pan down"
                    class="flex h-9 w-9 items-center justify-center rounded hover:bg-white/20"
                    onClick={() => panImage(0, panStep)}
                  >
                    <ArrowDownIcon size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Pan right"
                    class="flex h-9 w-9 items-center justify-center rounded hover:bg-white/20"
                    onClick={() => panImage(panStep, 0)}
                  >
                    <ArrowRightIcon size={16} />
                  </button>
                </div>
                <button
                  type="button"
                  aria-label="Close image viewer"
                  class="flex h-11 min-w-11 items-center justify-center rounded border border-white/20 bg-white/10 px-3 hover:bg-white/20"
                  onClick={closeImageModal}
                  data-testid="message-image-close"
                >
                  <CloseIcon size={18} color="currentColor" />
                </button>
              </div>
              <div
                ref={imageViewportRef}
                class="overflow-auto p-3"
                style={{ height: imageViewportHeight() }}
              >
                <div class="flex min-h-full items-center justify-center">
                  <img
                    src={image().url}
                    alt={image().altText ?? ''}
                    class="block origin-center object-contain transition-[width,height] duration-150"
                    style={{
                      width:
                        image().width != null
                          ? `${Math.max((image().width ?? 0) * zoom(), 1)}px`
                          : 'auto',
                      height:
                        image().height != null
                          ? `${Math.max((image().height ?? 0) * zoom(), 1)}px`
                          : 'auto',
                      'max-width': image().width != null ? 'none' : '100%',
                      'max-height': image().height != null ? 'none' : '100%',
                    }}
                    ref={(loadedImage) => {
                      if (
                        !(loadedImage instanceof HTMLImageElement) ||
                        !loadedImage.complete
                      ) {
                        return;
                      }
                      applyNaturalImageSize(loadedImage);
                    }}
                    onLoad={(event) => {
                      applyNaturalImageSize(event.currentTarget);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </Show>
      </BasicModal>
      <div class="flex flex-col gap-1">
        <For each={blocks()}>
          {(block) => (
            <BlockView
              block={block}
              payload={props.payload}
              onNavigate={onNavigate}
              onOpenImage={onOpenImage}
            />
          )}
        </For>
      </div>
      <Show
        when={(props.payload.entityLinks ?? []).some((link) => link.actionUrl)}
      >
        <div class="mt-2 flex flex-wrap gap-1">
          <For
            each={(props.payload.entityLinks ?? []).filter(
              (link) => link.actionUrl,
            )}
          >
            {(link) => (
              <button
                type="button"
                class="rounded-full bg-white/70 px-2 py-0.5 text-[11px] font-medium text-[#026EA1] hover:underline"
                data-testid={`entity-link-${link.type}-${link.entityId}`}
                onClick={() => onNavigate(link.actionUrl!)}
              >
                {link.type} {link.entityId}
              </button>
            )}
          </For>
        </div>
      </Show>
      <Show when={attachments().length > 0}>
        <div class="mt-1">
          <For each={attachments()}>
            {(attachment) => <AttachmentRow attachment={attachment} />}
          </For>
        </div>
      </Show>
    </Box>
  );
};
